import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Link } from 'react-router-dom';
import InviteModal from '@/components/feature/InviteModal';
import {
  getFamilyMembers,
  getProfileStats,
  type FamilyMember,
  type ProfileStats,
} from '@/services/familyService';

const EMPTY_STATS: ProfileStats = { albumCount: 0, photoCount: 0, recordDays: 0 };

function MemberAvatar({ member }: { member: FamilyMember }) {
  const [failed, setFailed] = useState(false);
  if (!member.avatar || failed) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-accent-100 text-accent-600 font-bold text-xl">
        {member.name.slice(0, 1)}
      </div>
    );
  }
  return (
    <img
      src={member.avatar}
      alt={member.name}
      className="w-full h-full object-cover"
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

export default function ProfilePage() {
  const { user } = useAuth();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [stats, setStats] = useState<ProfileStats>(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [memberData, statData] = await Promise.all([
          getFamilyMembers(),
          getProfileStats(),
        ]);
        if (cancelled) return;
        setMembers([...memberData].sort((a, b) => a.sortOrder - b.sortOrder));
        setStats(statData);
        setLoadError('');
      } catch (error) {
        if (cancelled) return;
        setLoadError(error instanceof Error ? error.message : '가족 정보를 불러오지 못했습니다.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="flex min-h-screen flex-col pb-8">
      <header className="px-4 pb-6 pt-12">
        <div className="flex items-start justify-between gap-4">
          <div>
            <span className="text-xs font-semibold uppercase tracking-wide text-accent-500">프로필</span>
            <h1 className="mt-1 text-2xl font-bold leading-tight text-foreground-950 font-heading">
              {user?.familyName || '우리 가족'}
            </h1>
            <p className="mt-2 text-sm text-foreground-500">함께 웃고 함께 자라는 우리 가족 이야기</p>
          </div>
          <Link
            to="/settings"
            className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-secondary-100 text-foreground-700 transition-colors hover:bg-secondary-200"
            aria-label="설정 열기"
          >
            <i className="ri-settings-3-line text-xl" />
          </Link>
        </div>

        {user && (
          <div className="mt-5 flex items-center justify-between rounded-2xl border border-background-200 bg-background-50 px-4 py-3.5">
            <div>
              <p className="text-xs text-foreground-400">로그인 사용자</p>
              <p className="mt-0.5 text-sm font-semibold text-foreground-900">{user.name}</p>
            </div>
            <Link to="/settings" className="text-xs font-semibold text-accent-600">계정·앱 설정</Link>
          </div>
        )}
      </header>

      {loadError && (
        <div className="mx-4 mb-4 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-600">{loadError}</div>
      )}

      <section className="px-4 pb-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-bold text-foreground-900">가족 구성원</h2>
          {user?.role === 'admin' && (
            <Link to="/admin/family-members" className="text-xs font-semibold text-accent-600">관리</Link>
          )}
        </div>
        <div className="grid grid-cols-3 gap-3">
          {loading && [...Array(6)].map((_, index) => (
            <div key={index} className="h-44 animate-pulse rounded-2xl bg-secondary-50" />
          ))}
          {!loading && members.map((member) => (
            <div key={member.id} className="flex flex-col items-center rounded-2xl bg-secondary-50 p-3 text-center">
              <div className="mb-2.5 h-16 w-16 flex-shrink-0 overflow-hidden rounded-full bg-secondary-200">
                <MemberAvatar member={member} />
              </div>
              <div className="mb-1.5 flex items-center gap-0.5">
                {[...Array(5)].map((_, index) => (
                  <div key={index} className="flex h-3 w-3 items-center justify-center">
                    <i className="ri-heart-fill text-[10px] text-accent-400" />
                  </div>
                ))}
              </div>
              <p className="max-w-full truncate text-sm font-bold text-foreground-900">{member.name}</p>
              <p className="mt-0.5 max-w-full truncate text-[11px] text-foreground-500">{member.role}</p>
              <p className="mt-2 text-[10px] text-foreground-400">{member.birthDate || '생일 미등록'}</p>
            </div>
          ))}
          {!loading && members.length === 0 && (
            <div className="col-span-3 py-10 text-center text-sm text-foreground-400">등록된 가족 구성원이 없습니다.</div>
          )}
        </div>
      </section>

      <section className="px-4 py-4">
        <div className="rounded-2xl bg-secondary-50 p-5">
          <h2 className="mb-3 text-sm font-bold text-foreground-900">우리 가족 기록</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-xl font-bold text-accent-500 font-heading">{stats.albumCount.toLocaleString()}</p>
              <p className="mt-0.5 text-[11px] text-foreground-500">앨범</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-accent-500 font-heading">{stats.photoCount.toLocaleString()}</p>
              <p className="mt-0.5 text-[11px] text-foreground-500">사진</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-accent-500 font-heading">{stats.recordDays.toLocaleString()}</p>
              <p className="mt-0.5 text-[11px] text-foreground-500">기록 일수</p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-2">
        <h2 className="mb-2 px-1 text-xs font-semibold text-foreground-400">가족 관리</h2>
        <div className="overflow-hidden rounded-2xl border border-background-200 bg-background-50">
          <button
            type="button"
            onClick={() => setShowInviteModal(true)}
            className="flex w-full items-center justify-between px-4 py-4 text-left transition-colors hover:bg-secondary-50"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-100">
                <i className="ri-user-add-line text-accent-600" />
              </div>
              <div>
                <span className="block text-sm font-medium text-foreground-900">가족 초대하기</span>
                <span className="mt-0.5 block text-[11px] text-foreground-400">초대 링크와 코드로 가족을 연결합니다</span>
              </div>
            </div>
            <i className="ri-arrow-right-s-line text-foreground-400" />
          </button>

          {user?.role === 'admin' && (
            <Link
              to="/admin/family-members"
              className="flex w-full items-center justify-between border-t border-background-200 px-4 py-4 transition-colors hover:bg-secondary-50"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-100">
                  <i className="ri-group-line text-accent-600" />
                </div>
                <div>
                  <span className="block text-sm font-medium text-foreground-900">가족 구성원 관리</span>
                  <span className="mt-0.5 block text-[11px] text-foreground-400">이름, 역할, 생일과 프로필 사진을 관리합니다</span>
                </div>
              </div>
              <i className="ri-arrow-right-s-line text-foreground-400" />
            </Link>
          )}
        </div>
      </section>

      <section className="px-4 py-4">
        <Link
          to="/settings"
          className="flex w-full items-center justify-between rounded-2xl bg-foreground-900 px-4 py-4 text-white"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10">
              <i className="ri-settings-3-line text-xl" />
            </div>
            <div>
              <span className="block text-sm font-semibold">설정</span>
              <span className="mt-0.5 block text-[11px] text-white/65">계정, 알림, 앱 설치와 관리자 설정</span>
            </div>
          </div>
          <i className="ri-arrow-right-s-line text-xl text-white/70" />
        </Link>
      </section>

      <InviteModal isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} />
    </div>
  );
}
